import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";

/**
 * Compatibility adapter.
 *
 * PublicARExperience used to own a second WebXRSpatialRuntime, camera lifecycle,
 * capture controls and AR chrome. Prompt 4 converges that ownership into the
 * canonical UniversalDoARSpace. Existing TheDoAR/ToDoAR callers keep this
 * component name temporarily so public routes do not need a flag-day rewrite.
 */
const PublicARExperience = forwardRef(function PublicARExperience(props, ref) {
  const [UniversalDoARSpace, setComponent] = useState(null), [loading, setLoading] = useState(false);
  const runtime = useRef(null), pending = useRef(null), epoch = useRef(0);
  const latest = useRef(props); latest.current = props;
  useEffect(() => () => { epoch.current += 1; }, []);
  useImperativeHandle(ref, () => ({
    start() {
      if (runtime.current) return runtime.current.start();
      if (pending.current) return pending.current;
      const request = epoch.current;
      setLoading(true);
      const work = import('../../core/workspace/ar/UniversalDoARSpace').then(module => {
        if (epoch.current !== request) return false;
        // Mount the existing runtime before starting; it remains the sole
        // permission/session owner. Failed loading never requests a camera.
        flushSync(() => { setComponent(() => module.default); setLoading(false); });
        return runtime.current?.start() || false;
      }).catch(error => {
        if (epoch.current === request) latest.current.onFailure?.(error);
        return false;
      }).finally(() => { if (pending.current === work) pending.current = null; if (epoch.current === request) setLoading(false); });
      pending.current = work;
      return work;
    },
    stop() { epoch.current += 1; setLoading(false); return runtime.current?.stop(); },
  }), []);
  return <>{loading ? <span role="status">Preparing AR…</span> : null}{UniversalDoARSpace ? <UniversalDoARSpace ref={runtime} {...props}/> : null}</>;
});

export default PublicARExperience;
